import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useAILearningFeedback } from '@/hooks/useAILearningFeedback';
import { Brain, TrendingUp, CheckCircle, AlertCircle, Target } from 'lucide-react';
import { format } from 'date-fns';

export function AILearningDashboard() {
  const { feedback, isLoading, getFeedbackStats } = useAILearningFeedback();
  
  if (isLoading) {
    return <div className="p-4">Loading AI learning data...</div>;
  }

  const stats = getFeedbackStats();
  const recentFeedback = feedback.slice(0, 5);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Brain className="h-6 w-6 text-primary" />
        <h2 className="text-2xl font-semibold">AI Learning Dashboard</h2>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Feedback</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalFeedback}</div>
            <p className="text-xs text-muted-foreground">
              Learning interactions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Corrections Made</CardTitle>
            <AlertCircle className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.corrections}</div>
            <p className="text-xs text-muted-foreground">
              Data corrections provided
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Validations</CardTitle>
            <CheckCircle className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.validations}</div>
            <p className="text-xs text-muted-foreground">
              Correct extractions confirmed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Improvement</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.avgConfidenceImprovement > 0 ? '+' : ''}{(stats.avgConfidenceImprovement * 100).toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Confidence increase
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Learning Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Learning Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Correction Rate</span>
              <span>{stats.totalFeedback > 0 ? ((stats.corrections / stats.totalFeedback) * 100).toFixed(1) : 0}%</span>
            </div>
            <Progress value={stats.totalFeedback > 0 ? (stats.corrections / stats.totalFeedback) * 100 : 0} />
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Validation Success</span>
              <span>{stats.totalFeedback > 0 ? ((stats.validations / stats.totalFeedback) * 100).toFixed(1) : 0}%</span>
            </div>
            <Progress value={stats.totalFeedback > 0 ? (stats.validations / stats.totalFeedback) * 100 : 0} />
          </div>
        </CardContent>
      </Card>

      {/* Recent Feedback */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Learning Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {recentFeedback.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No feedback provided yet. Start by reviewing extracted data to help train the AI.
            </p>
          ) : (
            <div className="space-y-3">
              {recentFeedback.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={item.feedback_type === 'correction' ? 'destructive' : 'default'}>
                        {item.feedback_type}
                      </Badge>
                      <span className="text-sm font-medium">
                        {(item.original_extraction as any)?.supplier_name || (item.original_extraction as any)?.customer_name_raw || 'Unknown'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(item.created_at), 'MMM dd, yyyy HH:mm')}
                    </p>
                  </div>
                  
                  {item.confidence_before && item.confidence_after && (
                    <div className="text-right">
                      <div className="text-sm font-medium">
                        {((item.confidence_after - item.confidence_before) * 100).toFixed(1)}%
                      </div>
                      <div className="text-xs text-muted-foreground">improvement</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}